<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ref, computed, unref, toRef, watch } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useForm } from 'vee-validate';
import { Notify } from 'quasar';
import { useQuasar } from 'quasar';

import DynamicTabs from '@/components/DynamicTabs.vue';
import FormActions from '@/components/FormActions.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import StandardInput from '@/components/StandardInput.vue';
import StandardToggle from '@/components/StandardToggle.vue';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import LocalizedVideoInput from '@/components/base/LocalizedVideoInput.vue';
import BaseDateInput from '@/components/base/BaseDateInput.vue';
import ConsultationViewProjectList from '@/components/ConsultationViewProjectList.vue';
import ImageField from '@/components/ImageField.vue';
import ContextList from '@/components/ContextList.vue';
import SectionList from '@/components/SectionList.vue';
import LanguageSelector from '@/components/LanguageSelector.vue';
import UserPermissionList from '@/components/UserPermissionList.vue';
import EmailList from '@/components/EmailList.vue';
import consultationCompletedBanner from '@/components/consultationCompletedBanner.vue';
import GuidedConsultationTab from '@/components/ConsultationTabs/GuidedConsultationTab.vue';

import { useConsultationsStore, useConsultationTemplatesStore, useCommunityStore } from '@/stores';
import { Constants } from '@/constants';
import type { ConsultationDto, Locales, LocalizableString, UserPermissionDto } from '@/services/types/api.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import { provideEditLocale } from '@/composables/data-display-utils';
import {
  dynamicTabsNavigationGuard,
  formSaveImageGuard,
  formSaveNavigationGuard,
  resetFormToItem,
  type DynamicTabsNavigationGuardCallbacks,
} from '@/helpers/form.helper';
import { getSupportedLocales } from '@/plugins/i18n';
import { useLanguageValidationErrors } from '@/composables';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { consultationValidationSchema } from '@/validation/consultationSchema';

// CONST
const { t } = useI18n();
const $q = useQuasar();
const route = useRoute();

// REFS
const addVideo = ref<boolean>(false);

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  consultationId: number;
  isTemplate: boolean;
}>();

// STORE
const communityStore = useCommunityStore();
const { community } = storeToRefs(communityStore);
const consultationsStore = props.isTemplate ? useConsultationTemplatesStore() : useConsultationsStore();

// LIFECYCLE
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));

const validationSchema = computed(() =>
  consultationValidationSchema({
    languageEnabled: props.languageEnabled,
    communityLanguageEnabled: community?.value?.language_enabled ?? props.languageEnabled,
    isTemplate: props.isTemplate,
  }),
);

const formContext = useForm({ validationSchema });
const { handleSubmit, errors, meta, values, resetForm, validateField, setFieldValue } = formContext;

const languageErrors = useLanguageValidationErrors(errors);

const consultation = useWatchItemLoading<ConsultationDto>(
  toRef(props, 'consultationId'),
  consultationsStore,
  (item) => {
    resetFormToItem(formContext, item);
    addVideo.value = !!(item.welcome_video?.fr || item.welcome_video?.en);
  },
);

watch(addVideo, (enabled) => {
  if (enabled) return;
  // Ignore the transition that comes from loading a consultation that simply has no video.
  if (!consultation.value?.welcome_video) return;
  setFieldValue('welcome_video', null);
  setFieldValue('welcome_video_type', null);
});

onBeforeRouteLeave(formSaveNavigationGuard(meta));

// COMPUTED
const statusOptions = computed(() => consultationsStore.getConsultationStatuses());

const privateEmailLimit = Constants.Consultation.PrivateEmailLimit;

const isPrivateEmailLimitExceeded = computed(() => {
  return (values.private_email_allowed?.email?.length ?? 0) > privateEmailLimit;
});

const privateEmailCount = computed(() => {
  return values.private_email_allowed?.email?.length ?? 0;
});

const isDirty = computed(() => {
  return unref(meta).dirty;
});

const tabs = computed(() => {
  const tabList = [
    { id: 'infos', name: t('consultation.tabs.infos'), isDirty: isDirty.value, icon: 'o_info' },
    { id: 'sections', name: t('consultation.tabs.sections'), icon: 'o_folder' },
    { id: 'images', name: t('consultation.tabs.images'), icon: 'o_image' },
    { id: 'contexts', name: t('consultation.tabs.contexts'), icon: 'link' },
    community.value?.options?.consultation?.guided_consultation
      ? { id: 'guided-consultation', name: t('consultation.tabs.guidedConsultation'), icon: 'o_quiz' }
      : undefined,
    { id: 'options', name: t('consultation.tabs.options'), icon: 'more_vert' },
    !props.isTemplate ? { id: 'permissions', name: t('consultation.tabs.allowed_user'), icon: 'groups' } : undefined,
  ].filter((x) => x);
  return tabList;
});

// #<tabId> opens a given tab, e.g. the question editor's "Go to guided consultation" link; an unknown one opens the first
const openedTab = tabs.value.find((tab) => tab?.id === route.hash.substring(1))?.id ?? 'infos';

defineExpose({
  executeNavigationGuard,
});

// FUNCTIONS
const handleSaveBanner = async (options: SavingOptions) => {
  await consultationsStore.saveBanner(props.consultationId, options);
};

const handleSaveOpenGraph = async (options: SavingOptions) => {
  await consultationsStore.saveOpenGraph(props.consultationId, options);
};

const removeConsultationBanner = async () => {
  await consultationsStore.removeBanner(props.consultationId);
};

const removeConsultationOpenGraph = async () => {
  await consultationsStore.removeOpenGraph(props.consultationId);
};

const handleClearBanner = async () => {
  formSaveImageGuard(isDirty.value, removeConsultationBanner);
};
const handleClearOpenGraph = async () => {
  formSaveImageGuard(isDirty.value, removeConsultationOpenGraph);
};

const handleStatusChange = (status: number) => {
  if (status === Constants.Consultation.Status.Ongoing) {
    validateField('begin_date');
  }
};

const onSubmit = handleSubmit(async (values) => {
  const payload = { ...values, ...(addVideo.value ? {} : { welcome_video: null, welcome_video_type: null }) };
  console.debug('submitting', payload);
  wrapSavingOperation(
    async () => await consultationsStore.save(payload as ConsultationDto),
    () => resetForm({ values: consultation.value! }),
  );
});

const onCancel = () => {
  if (consultation.value) {
    resetForm({ values: consultation.value });
  } else {
    console.warn('onCancel: consultation is null');
  }
};

const fetchUserPermissions = async () => {
  if (!consultation.value) {
    return [];
  }

  if (!consultation.value.user_permissions) {
    await consultationsStore.fetchUserPermissions(props.consultationId);
  }

  // get the consultation object straight from the store since we are using a clone in the rest of the page
  // how can we improve? could we encapsulate better? or refactor?
  const c = consultationsStore.getItem(props.consultationId);
  return c!.user_permissions;
};

const addUserPermission = async (email: string, role: number): Promise<boolean> => {
  const success = await consultationsStore.addUserPermission(props.consultationId, email, role);
  if (!success) {
    Notify.create({
      type: 'warning',
      message: t('consultation.permission.alreadyAdmin'),
      position: 'top',
      timeout: 5000,
    });
  }
  return true;
};

const deleteUserPermission = async (up: UserPermissionDto) => {
  await consultationsStore.deleteUserPermission(up);
};

function updateEmailList(emails: string[]) {
  setFieldValue('private_email_allowed.email', emails);
}

function resetContent() {
  if (!values.conclusion_message.enabled) {
    const locales = getSupportedLocales();
    const content = locales.reduce((acc, locale) => {
      acc[locale] = '';
      return acc;
    }, {} as LocalizableString);
    setFieldValue('conclusion_message.content', content);
  }
}

const removeVote = async () => {
  $q.dialog({
    title: t('global.dialogs.removeVoteTitle', { type: t('section.name') }),
    message: t('global.dialogs.removeVoteMessage', { type: t('section.name') }),
    ok: {
      label: t('global.yes'),
      color: 'negative',
      flat: true,
    },
    cancel: {
      label: t('global.no'),
      color: 'primary',
      unelevated: true,
    },
  }).onOk(async () => {
    await wrapSavingOperation(async () => consultationsStore.removeVote(props.consultationId));
  });
};

function executeNavigationGuard(callbackFns?: DynamicTabsNavigationGuardCallbacks) {
  dynamicTabsNavigationGuard(meta, callbackFns);
}
</script>
<template>
  <div class="consultation-create-tab" v-if="consultation">
    <consultation-completed-banner
      v-if="!isTemplate && consultationsStore.isCompleted(consultationId)"
      :status="consultation.status"
    />
    <dynamic-tabs :tabs="tabs" :active="openedTab" keep-alive>
      <template #infos>
        <form-actions
          :onCancel="onCancel"
          :onSubmit="onSubmit"
          :languageErrors="languageErrors"
          :meta="meta"
          v-model:editLocale="editLocale"
          :languageEnabled="languageEnabled"
        />
        <localized-input
          name="title"
          :label="t('consultation.title')"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
        <standard-input
          name="slug"
          :label="t('consultation.slug')"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
        <q-field color="dark" :label="t('consultation.description')" stack-label borderless class="q-mb-md">
          <template v-slot:default>
            <localized-editor
              name="description"
              :label="t('consultation.description')"
              class="q-mt-sm full-width"
              outlined
              :editLocale="editLocale"
              :language_enabled="languageEnabled"
            />
          </template>
        </q-field>
        <div class="toggles">
          <standard-toggle name="attachmentsOpened" :label="t('global.attachments-opened')" outlined />
          <standard-toggle v-model="addVideo" :label="t('consultation.addVideo')" outlined />
        </div>
        <div v-if="addVideo">
          <localized-video-input
            name="welcome_video"
            type-name="welcome_video_type"
            :label="t('consultation.video')"
            :language_enabled="languageEnabled"
          />
        </div>
        <localized-select
          v-if="!props.isTemplate"
          name="status"
          :label="t('consultation.status.label')"
          :editLocale="editLocale"
          :options="statusOptions"
          emit-value
          map-options
          outlined
          @change="handleStatusChange"
        />
        <div class="row q-gutter-md" v-if="!props.isTemplate">
          <div class="col">
            <base-date-input name="begin_date" :label="t('consultation.begin_date')" />
          </div>
          <div class="col">
            <base-date-input name="end_date" :label="t('consultation.end_date')" />
          </div>
        </div>
        <div class="row q-gutter-md q-mb-md">
          <div class="col">
            <standard-toggle
              v-if="!props.isTemplate"
              name="options.automatic_publish"
              :label="t('consultation.options.automatic_publish')"
              outlined
            />
            <standard-toggle
              name="conclusion_message.enabled"
              :label="t('consultation.options.add-thank-you-message.toggle')"
              @change="resetContent"
              outlined
            />
            <localized-input
              name="conclusion_message.content"
              :disabled="!values.conclusion_message.enabled"
              :label="t('consultation.options.add-thank-you-message.input')"
              :editLocale="editLocale"
              outlined
              :language_enabled="languageEnabled"
              type="textarea"
            />
          </div>
        </div>
        <div v-if="consultation.status === Constants.Consultation.Status.PreLaunch">
          <p>{{ t('consultation.pre-launch.title') }}</p>
          <q-btn color="primary" :label="t('consultation.pre-launch.reset')" @click="removeVote" />
        </div>
        <consultation-view-project-list
          v-if="!props.isTemplate"
          :projects="consultation.projects"
          :language-enabled="languageEnabled"
        />
      </template>
      <template #images>
        <form-actions
          :onCancel="onCancel"
          :onSubmit="onSubmit"
          :languageErrors="languageErrors"
          :meta="meta"
          v-model:editLocale="editLocale"
          :languageEnabled="languageEnabled"
        />
        <image-field
          :label="t('consultation.banner')"
          :source="consultation.banner_file?.url"
          :target-width="1920"
          :target-height="360"
          @clear-image="handleClearBanner"
          @save-image="handleSaveBanner"
          :form-dirty="isDirty"
          :language-enabled="languageEnabled"
          alt-text-name="banner_file.altText"
        />
        <image-field
          :label="t('global.label.social-media-image')"
          :source="consultation.open_graph_file?.url"
          :target-width="1200"
          :target-height="630"
          @clear-image="handleClearOpenGraph"
          @save-image="handleSaveOpenGraph"
          :form-dirty="isDirty"
          :language-enabled="languageEnabled"
          alt-text-name="open_graph_file.altText"
        />
      </template>
      <template #contexts>
        <context-list
          :parent="Constants.Context.Parent.Consultation"
          :parent-id="props.consultationId"
          :languageEnabled="languageEnabled"
        />
      </template>
      <template #sections>
        <section-list
          :is-template="props.isTemplate"
          :consultation-id="props.consultationId"
          :consultation="consultation"
          :languageEnabled="languageEnabled"
        />
      </template>
      <template #guided-consultation>
        <guided-consultation-tab
          v-model:editLocale="editLocale"
          :consultation="consultation"
          :language-enabled="languageEnabled"
          :on-cancel="onCancel"
          :on-submit="onSubmit"
          :language-errors="languageErrors"
          :meta="meta"
        />
      </template>
      <template #options>
        <form-actions
          :show-language-switcher="false"
          v-model:editLocale="editLocale"
          :onCancel="onCancel"
          :onSubmit="onSubmit"
          :languageErrors="languageErrors"
          :meta="meta"
          :languageEnabled="languageEnabled"
        />
        <q-page>
          <q-card flat class="q-mb-md">
            <q-card-section>
              <standard-toggle
                name="options.citizen_proposal"
                :disabled="!community?.options.consultation.citizen_proposal"
                :label="t('consultation.options.citizen_proposal')"
                outlined
              />
              <standard-toggle
                v-if="!!community?.options?.use_demographics"
                name="options.anonymous_comments"
                :label="t('consultation.options.anonymous_comments')"
                outlined
              />
            </q-card-section>
          </q-card>
          <q-card class="q-mb-md">
            <q-card-section>
              <q-item-label header class="text-h6">{{ t('consultation.languages') }}</q-item-label>
            </q-card-section>
            <q-separator />
            <q-card-section>
              <language-selector
                name="language_enabled"
                :label="t('consultation.language_enabled')"
                :language-enabled="community?.language_enabled"
              />
            </q-card-section>
          </q-card>
          <q-card class="q-mb-md" v-if="!props.isTemplate">
            <q-card-section>
              <q-item-label header class="text-h6">{{
                t('consultation.options.mailingListSectionTitle')
              }}</q-item-label>
            </q-card-section>
            <q-separator />
            <q-card-section>
              <standard-toggle name="options.mailingList" :label="t('consultation.options.mailingList')" outlined />
            </q-card-section>
          </q-card>
        </q-page>
      </template>
      <template #permissions>
        <form-actions
          :onCancel="onCancel"
          :onSubmit="onSubmit"
          :languageErrors="languageErrors"
          :meta="meta"
          v-model:editLocale="editLocale"
          :showLanguageSwitcher="false"
          :languageEnabled="languageEnabled"
        />
        <user-permission-list
          :fetchUserPermissions="fetchUserPermissions"
          :addUserPermission="addUserPermission"
          :deleteUserPermission="deleteUserPermission"
          :roles="[Constants.User.Role.EDITOR]"
        />
        <q-separator spaced="xl" />
        <p class="text-h6">{{ t('consultation.private.title') }}</p>
        <standard-toggle
          name="type"
          :label="t('consultation.private.invitation-only')"
          :true-value="Constants.Consultation.Type.Private"
          :false-value="Constants.Consultation.Type.Public"
          outlined
        />
        <standard-toggle
          name="options.show_private_everyone"
          :label="t('consultation.private.show-private-everyone')"
          outlined
        />
        <q-banner v-if="isPrivateEmailLimitExceeded" class="q-mt-md q-mb-md bg-amber-2 text-dark" rounded>
          <div class="row items-center q-col-gutter-sm no-wrap">
            <q-icon name="warning" color="warning" />
            <span>{{
              t('consultation.private.email.limitWarning', { limit: privateEmailLimit, count: privateEmailCount })
            }}</span>
          </div>
        </q-banner>
        <div v-else>
          <email-list v-model="values.private_email_allowed.email" :error="errors.emails" @update="updateEmailList" />
          <standard-input
            name="private_email_allowed.domainString"
            :label="t('consultation.private.domains')"
            type="textarea"
            outlined
          />
        </div>
      </template>
    </dynamic-tabs>
  </div>
</template>
<style lang="scss">
.consultation-create-tab {
  .toggles {
    margin-bottom: 16px;
  }
}
</style>
